import type {
  ChatAttachmentUpload,
  ChatConversationSummary,
  ChatMessage,
  ChatSendResponse,
  ChatStateResponse,
} from '@wundo/shared/chat.ts';
import { CHAT_UPLOAD_MIME_TYPES } from '@wundo/shared/attachments.ts';
import { maxChatAttachmentBytes } from '@wundo/shared/maxChatAttachmentBytes.ts';
import type { OnboardingNudgeMessageSource } from '@wundo/shared/onboardingNudge.ts';
import { apiFetch, apiFetchJson } from '@wundo/client/api.ts';
import { currentChatAnchor } from './chatAnchor.ts';

interface Paginated<T> {
  data: T[];
  meta: { totalItems: number; totalPages: number; currentPage: number };
}

/** Whether a sitting is still running. */
export async function fetchChatState(): Promise<ChatStateResponse> {
  return await apiFetchJson<ChatStateResponse>('/chat/conversation');
}

/** How many earlier conversations are listed at a time. */
export const CHAT_HISTORY_PAGE_SIZE = 30;

export interface ChatHistoryPage {
  conversations: ChatConversationSummary[];
  /** The page after this one, or null once the list has run out. */
  older: number | null;
}

/** Earlier conversations, most recently spoken in first. */
export async function fetchChatHistory(page: number): Promise<ChatHistoryPage> {
  const response = await apiFetchJson<Paginated<ChatConversationSummary>>(
    `/chat/conversations?page=${page}&limit=${CHAT_HISTORY_PAGE_SIZE}`,
  );

  return {
    conversations: response.data,
    older: page < response.meta.totalPages ? page + 1 : null,
  };
}

/** How many messages are read at a time, going back through a conversation. */
export const CHAT_PAGE_SIZE = 50;

export interface ChatMessagePage {
  /** Oldest first, the way the drawer reads. */
  messages: ChatMessage[];
  /** The page before this one, or null at the beginning of the conversation. */
  older: number | null;
}

/**
 * One page of a conversation, oldest message first.
 *
 * Page 1 is the most recent, because that is the page worth having when there
 * are many; each page after it goes further back. The API answers newest
 * first for the same reason, and the page is turned around here rather than in
 * every component that shows it.
 */
export async function fetchChatMessages(
  conversationId: string,
  page: number,
): Promise<ChatMessagePage> {
  const response = await apiFetchJson<Paginated<ChatMessage>>(
    `/chat/conversations/${conversationId}/messages?page=${page}&limit=${CHAT_PAGE_SIZE}`,
  );

  return {
    messages: [...response.data].reverse(),
    older: page < response.meta.totalPages ? page + 1 : null,
  };
}

/**
 * Report that Wundo's replies arrived, and — once the drawer has shown them —
 * that they were read.
 *
 * Nothing depends on these; they are what makes a conversation legible after
 * the fact, next to the same receipts Teams and Slack record. So a failed
 * report is swallowed rather than shown: a chat that works is not worth
 * interrupting over bookkeeping.
 */
export async function reportChatReceipt(
  conversationId: string,
  receipt: 'delivered' | 'read',
): Promise<void> {
  try {
    await apiFetch(`/chat/conversations/${conversationId}/actions/${receipt}`, {
      method: 'POST',
    });
  } catch {
    /* Bookkeeping. The conversation itself is unaffected. */
  }
}

/**
 * Send a message.
 *
 * Naming a conversation continues it; leaving it out lets the backend decide
 * whether the last sitting is still warm enough to carry on. `startNew` begins
 * a conversation regardless, and cannot be combined with naming one.
 *
 * The page the user is on rides along, read here rather than passed in: the
 * drawer does not know what it is sitting beside, and asking at the moment of
 * sending is what makes the answer true for this message and no other.
 *
 * `source` says the message came from a button rather than from a keyboard.
 * Two onboarding nudges are about asking Wundo something, and asking leaves no
 * row anywhere — so the button that asks says what it asked, and that is what
 * closes the nudge. Typing the same question is not counted.
 */
export async function sendChatMessage(
  text: string,
  options: {
    conversationId?: string;
    startNew?: boolean;
    attachmentIds?: string[];
    source?: OnboardingNudgeMessageSource;
  } = {},
): Promise<ChatSendResponse> {
  const anchor = currentChatAnchor();

  return await apiFetchJson<ChatSendResponse>('/chat/messages', {
    method: 'POST',
    body: JSON.stringify({
      text,
      ...(anchor ? { context: anchor } : {}),
      ...(options.source ? { source: options.source } : {}),
      ...(options.attachmentIds?.length ? { attachment_ids: options.attachmentIds } : {}),
      ...conversationTarget(options),
    }),
  });
}

function conversationTarget(options: {
  conversationId?: string;
  startNew?: boolean;
}): { start_new: true } | { conversation_id: string } | Record<string, never> {
  if (options.startNew) return { start_new: true };
  if (options.conversationId) return { conversation_id: options.conversationId };
  return {};
}

/** What the file picker offers, and what a drop is checked against. */
export const CHAT_UPLOAD_ACCEPT = CHAT_UPLOAD_MIME_TYPES.join(',');

/**
 * Whether this file can be shared at all.
 *
 * Checked here as well as on the server, so a file that was never going to
 * work fails while the user is still holding it rather than after a round
 * trip. The server decides in the end — it reads the bytes, and a name and a
 * type are the two things about a file that cost nothing to get wrong.
 */
export function chatUploadRefusal(file: File): 'type' | 'size' | null {
  if (!(CHAT_UPLOAD_MIME_TYPES as readonly string[]).includes(file.type)) {
    return 'type';
  }

  return file.size > maxChatAttachmentBytes(file.type) ? 'size' : null;
}

/**
 * Put one file where the next message can pick it up. Multipart, field `file`,
 * content type left to the browser so the boundary is right.
 */
export async function uploadChatAttachment(file: File): Promise<ChatAttachmentUpload> {
  const body = new FormData();
  body.append('file', file);

  return await apiFetchJson<ChatAttachmentUpload>('/chat/attachments', {
    method: 'POST',
    body,
  });
}
